import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import BrandWordmark from "@/components/BrandWordmark";
import { ADMIN_PATH } from "@/lib/adminRoutes";


// Routes that trial-expired users can still access (payment + onboarding + admin)
const TRIAL_EXEMPT = [
  "/billing/success",
  "/onboarding",
  "/plans",
  "/settings",
  ADMIN_PATH,
];

const isAdmin = (u) => u?.role === "admin" || u?.is_admin;

export default function ProtectedRoute({ children }) {
  const { user, loading, canAccessApp, collaboratorNoAccess, collaboratorWorkspaceBlocked, logout } = useAuth();
  const { pathname } = useLocation();

  if (loading && user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center space-y-3 animate-fade-in">
          <BrandWordmark size="xl" className="mx-auto" />
          <div className="label-eyebrow">Caricamento</div>
        </div>
      </div>
    );
  }

  if (!user) return <Navigate to="/login" state={{ from: pathname }} replace />;

  // ── Role-based middleware ──────────────────────────────────────────────────
  // Admin users: block access to the planner app, send to admin panel
  if (isAdmin(user) && pathname !== ADMIN_PATH && !pathname.startsWith("/billing")) {
    return <Navigate to={ADMIN_PATH} replace />;
  }
  // Planner users: block access to admin path, send to their dashboard
  if (!isAdmin(user) && pathname === ADMIN_PATH) {
    return <Navigate to="/dashboard" replace />;
  }

  // ── Collaboratore senza eventi attivi condivisi ───────────────────────────
  // L'accesso condiviso è terminato (evento concluso/archiviato). Mostra un
  // messaggio dedicato e riporta al login, senza mai passare da /plans.
  if (collaboratorNoAccess) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-6 px-6 bg-background text-center">
        <BrandWordmark size="sm" />
        <div className="max-w-sm space-y-3">
          <h1 className="serif text-2xl font-light">{collaboratorWorkspaceBlocked ? "Workspace da riattivare" : "Accesso condiviso terminato"}</h1>
          <p className="text-sm text-muted-foreground">
            {collaboratorWorkspaceBlocked
              ? "Il proprietario deve attivare o rinnovare l’abbonamento dello studio. Contattalo per riprendere il lavoro."
              : "Gli eventi condivisi con te si sono conclusi. Chiedi al proprietario di condividerti un nuovo evento, oppure registra un tuo account Aurea Pro."}
          </p>
          <button
            onClick={() => logout?.()}
            className="mt-2 inline-block rounded-sm border px-4 py-2 text-xs uppercase tracking-wider"
          >
            Torna al login
          </button>
        </div>
      </div>
    );
  }

  // ── Trial / subscription gate (planner only) ──────────────────────────────
  if (!isAdmin(user) && !canAccessApp && !TRIAL_EXEMPT.includes(pathname)) {
    return <Navigate to="/plans" replace />;
  }

  return children;
}
